import * as Api from '@ViewModels';
import { useQuery } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { LevStandardQueryMeta, reactQueryClient } from '../../web/ReactQueryProvider';

const ADMIN_INTEGRATION_STATUS_QUERY_KEY = 'admin-integration-status';

export type AdminIntegrationConfigurationRequirementField =
	| 'SyncUser'
	| 'AccountConnection'
	| 'Tenant'
	| 'CrmDomain'
	| 'ClientCredentials'
	| 'CustomFieldPrefixes'
	| 'Locations'
	| 'Agencies'
	| 'Integration';

export type AdminIntegrationConfigurationRequirementState = 'Missing' | 'NotConnected';

export type AdminIntegrationConfigurationRequirementActor = 'Admin' | 'Customer';

export interface IAdminIntegrationConfigurationRequirement {
	field: AdminIntegrationConfigurationRequirementField;
	state: AdminIntegrationConfigurationRequirementState;
	message: string;
	// "Admin" = resolvable in this admin view; "Customer" = customer must complete it
	// in their own Levitate app (OAuth flow or customer-facing settings).
	actor?: AdminIntegrationConfigurationRequirementActor;
}

export interface IAdminIntegrationConfigurationStatus {
	needsConfiguration: boolean;
	requirements: IAdminIntegrationConfigurationRequirement[];
}

export interface IAdminIntegrationStatus {
	configurationStatus: IAdminIntegrationConfigurationStatus;
}

export function useGetAdminIntegrationStatusQuery({
	impersonationContext,
	provider,
	enabled,
	meta,
}: {
	impersonationContext?: Api.IImpersonationContext;
	provider: Api.IntegrationProvider;
	enabled?: boolean;
	meta?: LevStandardQueryMeta;
}) {
	const userSession = useUserSession();
	return useQuery({
		queryFn: () => {
			return userSession.webServiceHelper.callAsync<IAdminIntegrationStatus>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `integrations/${provider}/status`,
				}),
				'GET'
			);
		},
		refetchOnWindowFocus: false,
		queryKey: [ADMIN_INTEGRATION_STATUS_QUERY_KEY, provider, impersonationContext],
		enabled,
		meta,
	});
}

export const invalidateAdminIntegrationStatusQuery = ({
	impersonationContext,
	provider,
}: {
	impersonationContext?: Api.IImpersonationContext;
	provider: Api.IntegrationProvider;
}) =>
	reactQueryClient.invalidateQueries({
		queryKey: [ADMIN_INTEGRATION_STATUS_QUERY_KEY, provider, impersonationContext],
	});
